import { GenerationStatus, MediaType } from '@repo/rpc-types-creativefabrica-modality-v1'
import type { OutputMedia } from '@repo/rpc-types-creativefabrica-modality-v1'
import {
  MessageActionRejectionReason,
  MessageActionState,
  MessageRole,
  MessageStatus,
  MessageType,
} from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type {
  Conversation,
  Message,
  MessageAction,
} from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type { GetConversationResponse } from '../gen/service/creativefabrica/studioagent/v1/service_pb'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type {
  MediaApprovalRejectionReason,
  MediaApprovalState,
} from '@repo/studio-components-chat-media/media-types'
import { faceFromCue } from './mascot-reply'
import { planFrom, textOptions, toolLabel } from './plan'
import type { ChatMessage, ConversationSummary, ConversationTurnState } from '../types'
import { questionFrom } from './agent-question'
import { friendlyFailureFor } from './friendly-failure'
import { chatMediaPreview } from './chat-media-preview'
import { readableChatText } from './readable-chat-text'

function approvalState(state: MessageActionState): MediaApprovalState | null {
  switch (state) {
    case MessageActionState.AWAITING_APPROVAL: {
      return 'awaiting'
    }
    case MessageActionState.APPROVED: {
      return 'approved'
    }
    case MessageActionState.REJECTED: {
      return 'rejected'
    }
    default: {
      return null
    }
  }
}

function mediaKind(mediaType: MediaType): 'image' | 'video' | 'audio' {
  switch (mediaType) {
    case MediaType.VIDEO: {
      return 'video'
    }
    case MediaType.AUDIO: {
      return 'audio'
    }
    default: {
      return 'image'
    }
  }
}

function mediaOutput(
  media: OutputMedia,
): Pick<NonNullable<ChatMessage['media']>, 'url' | 'thumbnailUrl' | 'posterUrl'> {
  // The wire `url` is a rendition. Drops and downloads follow the original file;
  // the card keeps painting the preview.
  switch (media.content.case) {
    case 'imageContent': {
      const image = media.content.value
      return { url: image.originalImageUrl || image.url, thumbnailUrl: image.previewUrl }
    }
    case 'videoContent': {
      const video = media.content.value
      return {
        url: video.originalVideoUrl || video.url,
        posterUrl: widestThumbnail(video.thumbnailUrls),
      }
    }
    case 'audioContent': {
      return { url: media.content.value.url }
    }
    default: {
      return {}
    }
  }
}

function widestThumbnail(
  thumbnails: readonly { url: string; width: number }[],
): string | undefined {
  const poster = [...thumbnails].sort((a, b) => b.width - a.width)[0]
  return poster?.url
}

const AGENT_GENERATION_ID = 'agent_generation_id'

type AssetIdentity = Pick<
  NonNullable<ChatMessage['media']>,
  | 'generationId'
  | 'agentGenerationId'
  | 'outputMediaId'
  | 'mime'
  | 'width'
  | 'height'
  | 'duration'
  | 'posterUrl'
>

function mediaFrom(message: Message): ChatMessage['media'] {
  if (message.type !== MessageType.MEDIA) {
    return undefined
  }
  const generation = message.generation
  if (!generation) {
    return { status: 'generating', kind: 'image' }
  }
  const output = generation.outputMedia[0]
  const kind = output ? mediaKind(output.mediaType) : 'image'
  if (
    generation.status === GenerationStatus.FAILED ||
    generation.status === GenerationStatus.CANCELLED
  ) {
    return { status: 'failed', kind, errorMessage: generation.error?.message || undefined }
  }
  if (generation.status !== GenerationStatus.COMPLETED || !output) {
    return { status: 'generating', kind }
  }
  const ready = { status: 'ready' as const, kind, ...mediaOutput(output) }
  return { ...ready, ...assetIdentity(generation, output), ...generationSettings(generation) }
}

function generationSettings(generation: NonNullable<Message['generation']>) {
  const model = generation.modelUsed.trim()
  const options = textOptions(generation.options)
  return {
    ...(model ? { model } : {}),
    ...(options.length > 0 ? { options } : {}),
  }
}

function assetIdentity(
  generation: NonNullable<Message['generation']>,
  output: OutputMedia,
): Partial<AssetIdentity> {
  const agentGenerationId = generation.metadata?.[AGENT_GENERATION_ID]
  return {
    ...(generation.generationId ? { generationId: generation.generationId } : {}),
    ...(agentGenerationId ? { agentGenerationId } : {}),
    ...(output.id ? { outputMediaId: output.id } : {}),
    ...contentIdentity(output),
  }
}

function contentIdentity(
  output: OutputMedia,
): Omit<Partial<AssetIdentity>, 'generationId' | 'outputMediaId'> {
  switch (output.content.case) {
    case 'imageContent': {
      return sizeIdentity(output.content.value)
    }
    case 'videoContent': {
      const video = output.content.value
      const aspectRatio = video.aspectRatio.trim()
      return {
        ...sizeIdentity(video),
        ...(video.durationSeconds > 0 ? { duration: video.durationSeconds } : {}),
        ...(aspectRatio ? { aspectRatio } : {}),
      }
    }
    case 'audioContent': {
      const audio = output.content.value
      const posterUrl = audio.coverImageUrl || widestThumbnail(audio.coverThumbnailUrls)
      return {
        ...(audio.mimeType ? { mime: audio.mimeType } : {}),
        ...(audio.durationSeconds > 0 ? { duration: audio.durationSeconds } : {}),
        ...(posterUrl ? { posterUrl } : {}),
      }
    }
    default: {
      return {}
    }
  }
}

function sizeIdentity(content: { mimeType: string; width: number; height: number }) {
  return {
    ...(content.mimeType ? { mime: content.mimeType } : {}),
    ...(content.width > 0 ? { width: content.width } : {}),
    ...(content.height > 0 ? { height: content.height } : {}),
  }
}

function approvalFrom(action: MessageAction | undefined): ChatMessage['approval'] {
  const state = action ? approvalState(action.state) : null
  if (!action || !state) {
    return undefined
  }
  const approval =
    action.action.case === 'mediaGenerationApproval' ? action.action.value : undefined
  const plan = approval?.generationPlan
  const approvedCoinAmount =
    action.approvedCoinAmount === undefined ? undefined : Number(action.approvedCoinAmount)
  const decidedAt = timestampToMs(action.decidedAt)
  return {
    state,
    coinCost: Number(action.quotedCoinAmount),
    ...(approvedCoinAmount !== undefined && Number.isFinite(approvedCoinAmount)
      ? { approvedCoinAmount }
      : {}),
    ...(decidedAt > 0 ? { decidedAt } : {}),
    prompt: plan?.prompt ?? '',
    rejection: rejectionFrom(action.rejectionReason),
    modelChoices: (approval?.modelChoices ?? []).map(choice => ({
      model: choice.model,
      label: choice.label,
      iconUrl: choice.iconUrl,
      suggested: choice.suggested,
    })),
    costInputs: (plan?.inputs ?? []).flatMap(input => {
      const ref = input.inputRef
      if (!input.role || !ref) {
        return []
      }
      return [
        {
          role: input.role,
          durationSeconds: ref.durationSeconds,
          widthPx: ref.width,
          heightPx: ref.height,
          fps: ref.fps,
        },
      ]
    }),
    plan: planFrom(approval?.outputMediaType ?? MediaType.UNSPECIFIED, plan),
  }
}

function rejectionFrom(
  reason: MessageActionRejectionReason,
): MediaApprovalRejectionReason | undefined {
  switch (reason) {
    case MessageActionRejectionReason.USER_REJECTED: {
      return 'user_rejected'
    }
    case MessageActionRejectionReason.INSUFFICIENT_BALANCE: {
      return 'insufficient_balance'
    }
    case MessageActionRejectionReason.PAYMENT_REQUIRED: {
      return 'payment_required'
    }
    case MessageActionRejectionReason.REQUEST_UNAVAILABLE: {
      return 'request_unavailable'
    }
    case MessageActionRejectionReason.SUPERSEDED: {
      return 'superseded'
    }
    case MessageActionRejectionReason.SUBSCRIPTION_REQUIRED: {
      return 'subscription_required'
    }
    default: {
      return undefined
    }
  }
}

export function normalizeConversationSummary(conversation: Conversation): ConversationSummary {
  const media = chatMediaPreview({ list: conversation })
  return {
    id: conversation.id,
    title: readableChatText(conversation.title),
    lastMessageSnippet: readableChatText(conversation.lastMessageSnippet),
    updatedAt: timestampToMs(conversation.updatedAt),
    ...(media.thumbnailUrls.length > 0 ? { thumbnailUrls: media.thumbnailUrls } : {}),
    ...(media.mediaCount > 0 ? { mediaCount: media.mediaCount } : {}),
  }
}

export function timestampToMs(timestamp?: Timestamp): number {
  if (!timestamp) {
    return 0
  }
  return Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1_000_000)
}

export function normalizeMessage(message: Message): ChatMessage {
  return {
    id: message.id,
    role: message.role === MessageRole.ASSISTANT ? 'assistant' : 'user',
    text: message.text,
    timestamp: timestampToMs(message.createdAt),
    expression: faceFromCue(message.expression),
    approval: approvalFrom(message.action),
    media: mediaFrom(message),
    isStreaming:
      message.status === MessageStatus.THINKING || message.status === MessageStatus.STREAMING,
    failed: message.status === MessageStatus.FAILED,
    failure: friendlyFailureFor(message.id),
    tools: message.toolEvents.map(event => ({
      id: event.toolCallId,
      label: toolLabel(event.tool, event.summary),
      tool: event.tool,
    })),
    question: questionFrom(message.question),
  }
}

export function normalizeTurnState(response: GetConversationResponse): ConversationTurnState {
  const { conversation } = response
  if (!conversation) {
    throw new Error('GetConversation returned no conversation')
  }
  return { busy: conversation.busy }
}
